/**
 * Fixed shell top bar: hamburger (mobile), wordmark, ProjectSwitcher,
 * TeamSwitcher (multi-team), Dispatch, Comms unread, theme toggle, Settings + user.
 */
import { Inbox, Menu, Moon, Settings, Sun, Monitor, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { sessionCommsApi } from '../services/commsApi';
import { getAppTitle } from '../utils/env';
import { routes } from '../utils/navigation';
import { ProjectSwitcher } from './ProjectSwitcher';
import { TeamSwitcher } from './TeamSwitcher';
import { IconButton } from './ui/IconButton';

type Props = {
  mobileNavOpen: boolean;
  onToggleMobileNav: () => void;
  showMenuButton: boolean;
};

/**
 * Fixed shell chrome: brand, project/team switchers, Dispatch, Comms badge, theme, Settings.
 * Polls inbox openCount (prefer) then unread every 15s for the Comms badge.
 * Hides Dispatch when AuthContext.isVisitor (kitchen JWT; API VISITOR_READ_ONLY is the backstop).
 */
export function TopBar({ mobileNavOpen, onToggleMobileNav, showMenuButton }: Props) {
  const { t } = useTranslation();
  const { user, isVisitor } = useAuth();
  const { preference, cyclePreference } = useTheme();
  const location = useLocation();
  const userLabel = (user?.name || '').trim() || user?.email || '';
  const [commsBadge, setCommsBadge] = useState(0);
  const [needsYou, setNeedsYou] = useState(false);

  // Prefer open needs-you asks; fall back to unread conversation count.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [inbox, unread] = await Promise.all([
          sessionCommsApi.inbox().catch(() => ({ openCount: 0, items: [] })),
          sessionCommsApi.unread().catch(() => ({
            unreadConversations: 0,
            unreadMessages: 0,
            conversations: [],
          })),
        ]);
        if (cancelled) return;
        const open = inbox.openCount || 0;
        if (open > 0) {
          setNeedsYou(true);
          setCommsBadge(open);
        } else {
          setNeedsYou(false);
          setCommsBadge(unread.unreadConversations || 0);
        }
      } catch {
        if (!cancelled) {
          setNeedsYou(false);
          setCommsBadge(0);
        }
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [location.pathname]);

  const ThemeIcon = preference === 'light' ? Sun : preference === 'dark' ? Moon : Monitor;

  return (
    <header
      className="fixed inset-x-0 top-0 z-[1001] flex h-[var(--shell-topbar)] items-center gap-3 border-b border-[var(--border)] bg-[var(--card)] px-3 md:px-4"
      aria-label={t('shell.topBar')}
    >
      {showMenuButton ? (
        <IconButton
          label={mobileNavOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          onClick={onToggleMobileNav}
          aria-expanded={mobileNavOpen}
          aria-controls="devtools-sidebar"
        >
          {mobileNavOpen ? <X size={20} aria-hidden /> : <Menu size={20} aria-hidden />}
        </IconButton>
      ) : null}

      <Link
        to={routes.home()}
        className="flex min-w-0 items-center gap-2 font-display text-sm font-semibold tracking-tight text-[var(--foreground)]"
      >
        <span className="h-7 w-7 shrink-0 rounded-xl bg-[var(--accent)]" aria-hidden />
        <span className="truncate">{getAppTitle()}</span>
      </Link>

      <ProjectSwitcher />
      <TeamSwitcher />

      <div className="flex-1" />

      {/* Dispatch creates plans — hide for kitchen visitor JWTs (API is the backstop). */}
      {!isVisitor ? (
        <Link
          to={routes.dispatch()}
          className="hidden rounded-md bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-[var(--primary-foreground)] sm:inline-flex"
          data-testid="topbar-dispatch"
        >
          {t('nav.dispatch')}
        </Link>
      ) : null}

      <Link
        to={needsYou ? `${routes.comms()}?attention=needsYou` : routes.comms()}
        className="relative inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-[var(--muted)] hover:bg-[var(--muted-surface)] hover:text-[var(--foreground)] active:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
        aria-label={t('nav.comms')}
        data-testid="topbar-comms"
      >
        <Inbox size={14} strokeWidth={2} aria-hidden />
        <span className="hidden sm:inline">{t('nav.comms')}</span>
        {commsBadge > 0 ? (
          <span
            className="inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold text-[var(--primary-foreground)]"
            data-testid="topbar-comms-badge"
            aria-label={
              needsYou
                ? t('comms.needsYouAria', { count: commsBadge })
                : t('comms.unreadAria', { count: commsBadge })
            }
          >
            {commsBadge}
          </span>
        ) : null}
      </Link>

      <IconButton
        label={t('shell.themeToggle', { theme: preference })}
        onClick={cyclePreference}
        data-testid="topbar-theme"
      >
        <ThemeIcon size={16} aria-hidden />
      </IconButton>

      {userLabel ? (
        <div className="flex min-w-0 items-center gap-2" data-testid="topbar-user">
          <span
            className="max-w-[30vw] truncate text-xs text-[var(--muted)] md:max-w-xs"
            title={user?.email || userLabel}
          >
            {userLabel}
          </span>
          <Link
            to={routes.settings()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-[var(--muted)] hover:bg-[var(--muted-surface)] hover:text-[var(--foreground)]"
            aria-label={t('nav.settings')}
            data-testid="topbar-settings"
          >
            <Settings size={14} strokeWidth={1.75} aria-hidden />
            <span className="hidden sm:inline">{t('nav.settings')}</span>
          </Link>
        </div>
      ) : null}
    </header>
  );
}
